import { useRef, useEffect, useState } from 'react'
import { useLocation, useOutlet } from 'react-router-dom'
import { cn } from '@/lib/utils'

interface AnimatedOutletProps {
  className?: string
}

/**
 * Animated wrapper for React Router's Outlet.
 * Provides a smooth fade + slide transition when routes change.
 */
export function AnimatedOutlet({ className }: AnimatedOutletProps) {
  const location = useLocation()
  const outlet = useOutlet()
  const [displayedOutlet, setDisplayedOutlet] = useState(outlet)
  const [isAnimating, setIsAnimating] = useState(false)
  const prevPathRef = useRef(location.pathname)
  const isInitialMount = useRef(true)

  useEffect(() => {
    // Skip animation on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      setDisplayedOutlet(outlet)
      prevPathRef.current = location.pathname
      return
    }

    // Only animate if the path actually changed
    if (prevPathRef.current !== location.pathname) {
      setIsAnimating(true)
      
      // Short delay to allow exit animation, then swap content
      const timeout = setTimeout(() => {
        setDisplayedOutlet(outlet)
        prevPathRef.current = location.pathname
        
        // Allow enter animation to play
        requestAnimationFrame(() => {
          setIsAnimating(false)
        })
      }, 150)

      return () => clearTimeout(timeout)
    } else {
      // Path didn't change but outlet did (e.g., lazy load completed)
      setDisplayedOutlet(outlet)
    }
  }, [location.pathname, outlet])

  return (
    <div
      className={cn(
        "transition-all duration-200 ease-out",
        isAnimating 
          ? "opacity-0 translate-y-2" 
          : "opacity-100 translate-y-0",
        className
      )}
    >
      {displayedOutlet}
    </div>
  )
}
